import { FC, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import cn from 'classnames';
import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { Button, createBemBlockBuilder, Loading } from '@ebsco-ui/ebsco-ui';

import {
  getErrorReason,
  HoldsAndCheckoutsMessage,
  Modal,
  ModalHeader,
  Toast,
} from '@app/components';
import { QUERY_KEYS, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { sharedMessages } from '@app/translations';

import { useRemoveHoldMutation } from './useRemoveHoldMutation';
import { removeHoldToastMessages } from './removeHoldToastMessages';

import css from './RemoveHoldModal.module.scss';
import modalHeaderCss from '../RecordSummaryModalHeader/RecordSummaryModalHeader.module.scss';
import '../Modal.scss';

interface RemoveHoldModalProps {
  isOpen: boolean;
  recordId: string;
  onModalClose: () => void;
  onError: ({ isError, errorMessage }) => void;
}

const cnBem = createBemBlockBuilder(['modal']);

export const RemoveHoldModal: FC<RemoveHoldModalProps> = ({
  isOpen,
  recordId,
  onModalClose,
  onError,
}) => {
  const queryClient = useQueryClient();
  const { $t } = useIntl();
  const removeHoldMutation = useRemoveHoldMutation();
  const { selectConfiguredOption } = useHoldsAndCheckoutsPageConfig();

  const removeHoldTitle = useMemo(
    () =>
      selectConfiguredOption(
        $t(sharedMessages.removeRequest),
        $t(sharedMessages.removeHold)
      ),
    [$t, selectConfiguredOption]
  );

  const removeHoldToastIcons = useMemo(
    () => ({
      success: faCheckCircle,
      error: faExclamationTriangle,
    }),
    []
  );

  useEffect(() => {
    const status = removeHoldMutation.status;

    if (status === 'success') {
      Toast({
        type: status,
        title: (
          <HoldsAndCheckoutsMessage
            requestsMessage={$t(removeHoldToastMessages.successRequest)}
            holdsMessage={$t(removeHoldToastMessages.successHold)}
          />
        ),
        icon: removeHoldToastIcons[status],
      });

      onModalClose();

      queryClient.refetchQueries([QUERY_KEYS.holdsAndCheckoutsPage], {
        type: 'active',
      });
      queryClient.refetchQueries([QUERY_KEYS.userItemStatuses], {
        type: 'active',
      });
    }

    if (status === 'error') {
      onError({
        isError: true,
        errorMessage: getErrorReason(removeHoldMutation),
      });
      onModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryClient,
    removeHoldMutation.status,
    removeHoldToastIcons,
    onModalClose,
  ]);

  const getModalHeader = () => {
    return (
      <>
        <h2 className={modalHeaderCss.headerTitle}>{removeHoldTitle}</h2>
        <div className={modalHeaderCss.headerBody}>
          <p data-auto="remove-hold-message" className={css.removeHoldMessage}>
            <HoldsAndCheckoutsMessage
              requestsMessage={$t({
                id: 'removeHoldModal.removeRequestMessage',
                defaultMessage:
                  'Are you sure you want to remove this request? This action cannot be undone.',
              })}
              holdsMessage={$t({
                id: 'removeHoldModal.removeHoldMessage',
                defaultMessage:
                  'Are you sure you want to remove this hold? This action cannot be undone.',
              })}
            />
          </p>
        </div>
      </>
    );
  };

  const getModalFooter = () => {
    return (
      <>
        <Button
          title={$t(sharedMessages.cancel)}
          styleType="text"
          onClick={onModalClose}
        >
          {$t(sharedMessages.cancel)}
        </Button>
        <Button
          title={$t({
            id: 'removeHoldModal.remove',
            defaultMessage: 'Remove',
          })}
          styleType="default"
          onClick={() => {
            removeHoldMutation.mutate({
              holdId: recordId,
            });
          }}
        >
          <FormattedMessage
            id="removeHoldModal.remove"
            defaultMessage="Remove"
          />
        </Button>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={removeHoldTitle}
      className={cn(cnBem(), cnBem('--remove'))}
      onClose={onModalClose}
    >
      <div
        className={cn({
          [cnBem('__disabledScroll')]: removeHoldMutation.isLoading,
        })}
      >
        <ModalHeader onClose={onModalClose}>{getModalHeader()}</ModalHeader>
        <Modal.Footer>{getModalFooter()}</Modal.Footer>
        {removeHoldMutation.isLoading && (
          <Loading
            loadingMessage={selectConfiguredOption(
              $t({
                id: 'removeHoldModal.removingRequest',
                defaultMessage: 'Removing request...',
              }),
              $t({
                id: 'removeHoldModal.removingHold',
                defaultMessage: 'Removing hold...',
              })
            )}
          />
        )}
      </div>
    </Modal>
  );
};
